import { ContractKeyGroup } from '@cube3/common/model/schema/resources/contract-key-group';
import {
  MetadataCategory,
  MetadataFormField
} from '@cube3/common/model/schema/resources/metadata-category';
import { makeValidator } from './makeValidatorFromSchema';
import { FieldType } from '../types/GenericFormTypes';
import { makeFormatter } from './makeFormatter';
import { makeParser, multiSelectParser } from './makeParser';
import { FORMAT_C3_YEAR_DATE } from '@cube3/common/model/schema/resources/metadata-field';
import { isMobile } from 'react-device-detect';

export const selectFieldRegexp = /select/; // /select\(.*?\)/;
export const multipleFieldRegexp = /multiple/; // /multiple\(.*?\)/;

const lookupResourcePicker = (schema) => {
  switch (schema.title) {
    case 'ContractProjects':
      return 'contract-projects';
    case 'ContractAssets':
      return 'contract-assets';
    case 'ContractRecipients':
      return 'contract-recipients';
    default:
      return undefined;
  }
};

// build additional data used by components like suggestions / options
export const makeData = (field: MetadataFormField) => {

  const schema = JSON.parse(field.json_schema);
  const type = field.value_type;

  const {
    minLength,
    maxLength,
    minimum,
    maximum,
    format,
    multipleOf,
    items = {},
    pattern,
    minDate
  } = schema;

  switch (true) {
    case type === 'number':
      return {
        min: minimum,
        max: maximum,
        step: multipleOf
      };
    case type === 'string':
      return {
        minLength,
        maxLength,
        pattern
      };
    case type === 'email':
      return {};
    case type === 'date':
      return {
        minLength,
        maxLength,
        yearOnly: format === FORMAT_C3_YEAR_DATE,
        minDate
      };
    case type === 'expires_at':
      return {
        minDate: minDate
      };
    case type === 'textarea':
      return { minLength, maxLength, multiline: schema.multiline };
    case type === 'multiselect':
      return {
        options: multiSelectParser(items.enum) // string[]
      }; // TODO:
    case type === 'resource_picker':
      return {
        variant: lookupResourcePicker(schema)
      }; // TODO:

    case type === 'waterfall_select':
      return {
        options: makeWaterfallOptions(schema)
      }; // TODO: min / max entries

    case type === 'checkbox':
      if (schema.type === 'boolean') {
        return {}
      } else {
        return {
          options: (items.enum || schema.enum)?.map(o => ({ value: o?.value || o, id: o?.id || o, })),
          maxLength: maxLength,
          multiple: schema.type === 'array'
        };
      }
    case !!selectFieldRegexp.test(type):
      return {
        options:
          schema.type === 'array' ? items.enum || items[0]?.enum : schema.enum,
        multiple: schema.type === 'array'
      }; // TODO: min / max entries

    // case !!multipleFieldRegexp.test(type):
    //   return {
    //     suggestions: ['i', "don't", 'know', 'where', 'to', 'get', 'these']
    //   }; // TODO: find where to get these
    default:
      return undefined;
  }
};

const makeWaterfallOptions = (schema) => {
  const { properties = {} } = schema || { properties: {} };

  console.info("DEBUG waterfall", properties, schema)

  return Object.keys(properties).reduce((acc, l1) => {
    acc[l1] = [...(properties[l1].items.enum || [])];
    // provide some hints for the placeholder
    // NOTE: bit ugly since it adds properties to an array instance, but it works for now
    acc[l1].optional = !properties[l1].minItems || properties[l1].minItems < 1;
    acc[l1].minItems = properties[l1].minItems;
    return acc;
  }, {});
};

const fieldOwnerCache = new Map();
export const clearFieldOwnerCache = () => {
  fieldOwnerCache.clear();
};

export const getFields = (category) => {
  return (
    category &&
    ((category.metadata_fields || category.keys) as MetadataFormField[])
  );
};

export const getFieldString = (category) =>
  category && category.hasOwnProperty('keys') ? 'key' : 'field';

export const makeMetadataFields = (
  category: MetadataCategory | ContractKeyGroup,
  /** force to be required (exp: youtube export fields) */
  forceRequired = false
) => {
  const fields = getFields(category);
  const fieldString = getFieldString(category);

  if (fields) {
    return fields.map((field) => {
      const {
        value_type,
        read_only,
        required,
        display_name,
        json_schema,
        ...restProps
      } = field;

      const isRequired = required || forceRequired;

      const id = `${fieldString}_${field.id}`;

      if (!fieldOwnerCache.has(id)) {
        fieldOwnerCache.set(id, { owner: category.id });
      }

      const foc = { ...fieldOwnerCache.get(id) };

      foc.owner = category.id;

      return {
        ...restProps,
        json_schema,
        type: field.value_type as FieldType,
        id: id, // workaround because redux form doesn't allow numeric field names
        name: id, // workaround because redux form doesn't allow numeric field names
        label: display_name,
        ...makeData(field),
        validate: makeValidator(isRequired, json_schema, value_type),
        parse: makeParser(value_type, json_schema),
        format: makeFormatter(value_type, json_schema),
        disabled: read_only,
        required: isRequired,
        owner: foc,
        fullWidth: value_type !== 'select',
        // used for select field, theme.spacing[80] = 320px
        width: isMobile ? 72 : 80
      };
    });
  }
};
